// Murano-leksikon — Farveladelands content hub om vintage Murano-glas.
// Skrevet i brand voice: warm, konkret hverdagsdansk på DA-siden, plain
// natural English på EN. Hver artikel er 600-900 ord — nok til at Google
// ser os som eksperter, ikke som en webshop der bare lister produkter.
//
// Body er almindelig tekst med tomme linjer mellem afsnit. Renderes ved at
// splitte på "\n\n". H2/H3 bruger linjer der starter med "## " / "### ".

export type LeksikonArticle = {
  slug: string;
  /** Kort kategori-mærke vist over titlen (f.eks. "Teknik", "Producent"). */
  kicker: { da: string; en: string };
  /** Cover-billede — Shopify CDN eller local asset. */
  cover: string;
  /** Læsetid i minutter, ca. */
  readingMinutes: number;
  da: { title: string; excerpt: string; body: string };
  en: { title: string; excerpt: string; body: string };
  /** Relaterede collection-handles til "Se i butikken"-blokken nederst. */
  shopHandles?: string[];
};

// Covers fra Shopify CDN — ægte produktfotos vi allerede har på lager.
const COVER_GUIDE =
  "https://cdn.shopify.com/s/files/1/0562/8516/7668/products/image_2a8e1f78-32a1-48c6-9528-c0bd493b629a.heic?v=1668872354&width=1600";
const COVER_MUSHROOM =
  "https://cdn.shopify.com/s/files/1/0562/8516/7668/products/image_205f0488-e9ce-418d-98dc-137637dd8442.heic?v=1661845295&width=1600";
const COVER_PEACOCK =
  "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/ACF51004-1EA8-4130-94B9-D4510CCE5BC9.jpg?v=1721731951&width=1600";
const COVER_PINK =
  "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/9CA28B55-7488-4A99-8BFD-51D5601B6B7D.jpg?v=1711471195&width=1600";
const COVER_CEILING =
  "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/D6E525C3-BAF9-4AC1-B9E3-8FAF884D388D.jpg?v=1710108862&width=1600";
const COVER_WALL =
  "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/0FEA5785-F6C0-4826-982A-78E425BD168C.jpg?v=1763556495&width=1600";

export const leksikonArticles: LeksikonArticle[] = [
  {
    slug: "saadan-kender-du-aegte-murano-glas",
    kicker: { da: "Guide", en: "Guide" },
    cover: COVER_GUIDE,
    readingMinutes: 7,
    shopHandles: ["murano-lamper", "murano-vaser", "murano-glaskunst"],
    da: {
      title: "Sådan kender du ægte Murano-glas",
      excerpt:
        "Loppemarkedets svære spørgsmål: Er det ægte Murano, eller er det bare italiensk glas der ligner? Her er de tegn vi selv kigger efter.",
      body: `Vi får spørgsmålet hver uge: "Hvordan ved I, det er ægte Murano?" Det korte svar er, at man sjældent kan være 100% sikker uden papirer. Det lange svar — som vi bruger på loppemarkedet — er en blanding af håndværk, mærker og mavefornemmelse efter mange år med glasset i hænderne.

## Pontilmærket i bunden

Mundblæst glas har næsten altid et lille ru mærke i bunden, hvor pontil-stangen sad mens glaspusteren formede toppen. På ægte Murano er det ofte slebet glat eller dækket af et lille mærkat. Hvis bunden er helt jævn og maskinpresset, er det ikke Murano — så er det fabriksglas.

## Klistermærker og labels

De fleste Murano-stykker fra 1950-80'erne havde et lille klistermærke: "Vetro Artistico Murano", "Made in Murano Italy" eller producentens eget logo (Vetri, Seguso, Mazzega, Venini). Mærkerne falder af med årene, så et stykke uden mærke er ikke automatisk falsk — men et stykke med original label er guld værd.

## Vægt og tykkelse

Murano-glas er typisk tungere end almindeligt fabriksglas. Tag det i hånden. Det skal føles substantielt, ikke skrøbeligt. Glassets vægge er ofte uregelmæssige i tykkelsen — det er en god ting, det betyder at det er håndlavet.

## Bobler og uperfektheder

Små luftbobler, en let asymmetri, en farve der ikke er helt jævn — det er kendetegn ved håndarbejde, ikke fejl. Maskinglas er for perfekt. Hvis to "ens" stykker er nøjagtigt identiske, er det sandsynligvis ikke Murano.

## Farverne

Murano-glaspustere har i århundreder brugt mineralpigmenter der giver dybe, lysende farver. Klassikerne: bourbon-rød (guld), turkis (kobber), gul (cadmium), rosa (selenium). Hvis farverne ligner billige plastikfarver — f.eks. neon-pink eller skarp lime — så er det sjældent Murano.

## Sidste check: hvor kommer det fra?

På loppemarkedet spørger vi altid sælgeren. Et stykke fra et dødsbo i Italien har en helt anden sandsynlighed end et stykke fra en kasse "diverse glas". Det betyder ikke alt — vi har fundet ægte Murano i de mest tilfældige kasser — men proveniens hjælper.

## Hvad vi gør hos Farveladeland

Vi køber kun ind, når vi selv er sikre. Hvis vi er i tvivl, skriver vi det. Du skal aldrig betale Murano-pris for noget vi ikke tror på selv. Og hvis du har et stykke derhjemme du er usikker på — kom forbi showroomet med det. Vi kigger gerne med.`,
    },
    en: {
      title: "How to spot real Murano glass",
      excerpt:
        "The flea market's hardest question: is it real Murano, or just Italian glass that looks like it? Here's what we look for ourselves.",
      body: `We get this question every week: "How do you know it's real Murano?" The short answer is that you rarely can be 100% sure without paperwork. The long answer — which we use at the flea market — is a mix of craftsmanship, marks, and gut feeling after years of handling the glass.

## The pontil mark on the bottom

Mouth-blown glass almost always has a small rough mark on the bottom where the pontil rod was attached while the glassblower shaped the top. On real Murano it's often ground smooth or covered by a small label. If the bottom is completely flat and machine-pressed, it's not Murano — it's factory glass.

## Stickers and labels

Most Murano pieces from the 1950s-80s had a small sticker: "Vetro Artistico Murano", "Made in Murano Italy", or the maker's own logo (Vetri, Seguso, Mazzega, Venini). The stickers fall off over the years, so a piece without one isn't automatically fake — but a piece with the original label is gold.

## Weight and thickness

Murano glass is usually heavier than ordinary factory glass. Pick it up. It should feel substantial, not flimsy. The walls are often uneven in thickness — that's a good sign, it means it was made by hand.

## Bubbles and imperfections

Small air bubbles, slight asymmetry, a colour that isn't perfectly even — these are signs of handwork, not flaws. Machine glass is too perfect. If two "matching" pieces are exactly identical, it's probably not Murano.

## The colours

Murano glassblowers have used mineral pigments for centuries to get those deep, glowing colours. Classics: bourbon red (gold), turquoise (copper), yellow (cadmium), pink (selenium). If the colours look like cheap plastic — neon pink, sharp lime — it's rarely Murano.

## Last check: where did it come from?

At the flea market we always ask the seller. A piece from an Italian estate sale has very different odds from a piece in a "miscellaneous glass" box. It doesn't decide everything — we've found real Murano in the most random boxes — but provenance helps.

## What we do at Farveladeland

We only buy when we're sure ourselves. When we're unsure, we say so. You should never pay Murano prices for something we don't believe in. And if you have a piece at home you're unsure about — drop by the showroom with it. We're happy to take a look.`,
    },
  },
  {
    slug: "de-store-murano-glasvaerksteder",
    kicker: { da: "Producenter", en: "Makers" },
    cover: COVER_PEACOCK,
    readingMinutes: 9,
    shopHandles: ["murano-lamper", "murano-loftlamper", "murano-vaeglamper"],
    da: {
      title: "De store Murano-glasværksteder, du skal kende",
      excerpt:
        "Vetri, Mazzega, Seguso, Venini, Barovier, Vistosi, La Murrina — syv navne der dukker op igen og igen, når man handler vintage Murano. Her er hvad de står for.",
      body: `Murano er ikke ét værksted — det er en hel ø med dusinvis af glasværksteder, store som små. Når du handler vintage, dukker de samme syv navne op igen og igen. Det er værd at kende dem, både for at vurdere et stykke og for at vide hvad du faktisk kigger på.

## 1. Vetri Murano

Det navn der står på flest af vores lamper. Vetri var et stort, etableret værksted der i 60'erne og 70'erne lavede mange af de loftlamper og pendler, vi sælger i dag. Klassiske swirl-mønstre, store mushroom-bordlamper, peacock-lampen. Hvis du leder efter en sikker introduktion til vintage Murano-belysning, er en Vetri-lampe et godt sted at starte.

## 2. Mazzega

Mazzega-værkstedet (grundlagt af Angelo Vittorio Mazzega) lavede nogle af de mest ikoniske mid-century lamper fra Murano. Karakteristiske geometriske former, ofte med tykt klart glas der ligger over farvet glas. Mazzega-lamper er sjældnere på markedet end Vetri og typisk dyrere.

## 3. Seguso Vetri d'Arte

Et af de ældste navne. Seguso har lavet glas på Murano siden 1397 (ja, virkelig). Deres alabastro-serie — opaliserende, mælkehvidt glas — er klassikere. Seguso-skåle og -muslinger fra 60'erne og 70'erne er stadig blandt de mest eftertragtede.

## 4. Venini

Det mest "designer" af de store værksteder. Venini samarbejdede med arkitekter og kunstnere som Carlo Scarpa, Tapio Wirkkala og Fulvio Bianconi. Venini-stykker er ofte signeret og kan koste fra 5.000 kr og opefter på auktioner. Hvis du finder en signeret Venini på loppemarkedet til 200 kr — løb.

## 5. Barovier & Toso

Barovier-familien har lavet glas på Murano siden 1295. Værkstedet er kendt for komplekse teknikker som intarsia (indlagt mønstret glas) og tessuto (tekstur-glas). Barovier-stykker er ofte tunge, skulpturelle og dyre.

## 6. Vistosi

Vistosi specialiserede sig i belysning fra 60'erne og frem. Mange Vistosi-lamper er skulpturelle og næsten arkitektoniske. Hvis du ser en mushroom-lampe der er lidt mere "designermøbel" end "loppefund" — der er stor chance for at det er en Vistosi.

## 7. La Murrina

Et yngre værksted (grundlagt 1969) der primært lavede belysning og lysekroner gennem 70'erne, 80'erne og 90'erne. La Murrina-lysekroner — store, dramatiske, ofte med blade af farvet glas — er en fast genganger i italienske villaer fra perioden.

## Hvad med uden mærke?

Mange Murano-stykker er aldrig signeret eller har mistet deres label. Det betyder ikke at det er mindre værd — der har været hundreder af mindre værksteder på øen, og meget af det smukkeste glas kommer derfra. Det vi sælger uden producentnavn, kalder vi typisk bare "vintage Murano" — og prissætter derefter.`,
    },
    en: {
      title: "The major Murano glassworks worth knowing",
      excerpt:
        "Vetri, Mazzega, Seguso, Venini, Barovier, Vistosi, La Murrina — seven names that come up again and again in vintage Murano. Here's what they stand for.",
      body: `Murano isn't one workshop — it's a whole island with dozens of glassworks, large and small. When you buy vintage, the same seven names appear again and again. It's worth knowing them, both to judge a piece and to understand what you're actually looking at.

## 1. Vetri Murano

The name on most of our lamps. Vetri was a large, established workshop that in the 60s and 70s made many of the ceiling lamps and pendants we sell today. Classic swirl patterns, big mushroom table lamps, the peacock lamp. If you're looking for a safe introduction to vintage Murano lighting, a Vetri lamp is a great place to start.

## 2. Mazzega

The Mazzega workshop (founded by Angelo Vittorio Mazzega) made some of the most iconic mid-century lamps from Murano. Distinctive geometric shapes, often with thick clear glass over coloured glass. Mazzega lamps are rarer on the market than Vetri, and typically more expensive.

## 3. Seguso Vetri d'Arte

One of the oldest names. Seguso has been making glass on Murano since 1397 (yes, really). Their alabastro series — opalescent, milky-white glass — are classics. Seguso bowls and shells from the 60s and 70s are still among the most sought-after.

## 4. Venini

The most "designer" of the major workshops. Venini collaborated with architects and artists like Carlo Scarpa, Tapio Wirkkala and Fulvio Bianconi. Venini pieces are often signed and can fetch from £500 upwards at auction. If you find a signed Venini at a flea market for £20 — run.

## 5. Barovier & Toso

The Barovier family has made glass on Murano since 1295. The workshop is known for complex techniques like intarsia (inlaid patterned glass) and tessuto (textured glass). Barovier pieces are typically heavy, sculptural and expensive.

## 6. Vistosi

Vistosi specialised in lighting from the 60s onwards. Many Vistosi lamps are sculptural and almost architectural. If you see a mushroom lamp that feels a little more "designer furniture" than "flea-market find" — there's a good chance it's a Vistosi.

## 7. La Murrina

A younger workshop (founded 1969) that primarily made lighting and chandeliers through the 70s, 80s and 90s. La Murrina chandeliers — large, dramatic, often with leaves of coloured glass — are a regular fixture in Italian villas from the period.

## What about unmarked pieces?

Many Murano pieces were never signed, or have lost their label. That doesn't mean they're worth less — there have been hundreds of smaller workshops on the island, and much of the most beautiful glass comes from them. What we sell without a maker's name we typically just call "vintage Murano" — and price accordingly.`,
    },
  },
  {
    slug: "sommerso-teknikken-forklaret",
    kicker: { da: "Teknik", en: "Technique" },
    cover: COVER_PINK,
    readingMinutes: 5,
    shopHandles: ["murano-vaser", "murano-glaskunst"],
    da: {
      title: "Sommerso-teknikken forklaret",
      excerpt:
        "Det er ordet du støder på igen og igen, når du læser om Murano-glas. Her er hvad sommerso egentlig betyder, og hvordan du kender det.",
      body: `Sommerso betyder bogstaveligt "nedsænket" på italiensk. Som teknik handler det om at lægge flere lag farvet glas inden i hinanden, så det færdige stykke har en farve indeni og en anden — typisk klar — udenpå.

## Hvordan det laves

Glaspusteren starter med en lille klump farvet glas. Den dyppes i smeltet glas af en anden farve, som lægger sig som et lag uden om. Det kan gentages flere gange. Til sidst dyppes det hele i klart glas, så de indre farver "svæver" inde i en gennemsigtig form.

Resultatet er et stykke der ser massivt og tungt ud — og er det. En sommerso-vase er ofte dobbelt så tung som en almindelig vase af samme størrelse, fordi det meste af volumen er solidt glas.

## Hvad du skal kigge efter

- **Klare lag uden bobler mellem farverne.** Hvis der er bobler eller ujævnheder mellem lagene, er det dårligt håndværk — eller fake.
- **En klar "kant" eller skygge** der følger overgangen mellem farverne. Det er signaturen.
- **Vægten.** Sommerso skal være tungt. Hvis det føles let, er det ikke ægte.

## Hvornår blev det populært

Sommerso-teknikken blev brugt fra 1930'erne, men nåede sit højdepunkt i 1950'erne og 60'erne. Seguso, Mandruzzato og Cenedese var blandt mestrene. Mange af de sommerso-stykker du finder på loppemarkedet i dag, er fra netop den periode.

## Sommerso vs. cased glass

Du støder også på "cased glass" — det er det engelske/amerikanske ord for omtrent samme idé. Forskellen er teknisk: cased glass har typisk to lag (en farve indeni, klart udenpå), mens ægte sommerso ofte har tre eller flere lag farve. Men i daglig tale bruges ordene ofte i flæng.`,
    },
    en: {
      title: "Sommerso technique explained",
      excerpt:
        "It's the word you keep running into when reading about Murano glass. Here's what sommerso actually means, and how to recognise it.",
      body: `Sommerso literally means "submerged" in Italian. As a technique it's about layering several colours of glass inside each other, so the finished piece has one colour inside and another — typically clear — outside.

## How it's made

The glassblower starts with a small lump of coloured glass. It's dipped in molten glass of a different colour, which forms a layer around it. This can be repeated several times. Finally the whole thing is dipped in clear glass, so the inner colours appear to "float" inside a transparent shape.

The result is a piece that looks massive and heavy — and is. A sommerso vase is often twice as heavy as an ordinary vase of the same size, because most of the volume is solid glass.

## What to look for

- **Clear layers with no bubbles between the colours.** Bubbles or unevenness between layers is bad craftsmanship — or fake.
- **A clear "edge" or shadow** that follows the transition between colours. That's the signature.
- **The weight.** Sommerso should be heavy. If it feels light, it isn't real.

## When it peaked

The sommerso technique was used from the 1930s, but reached its peak in the 1950s and 60s. Seguso, Mandruzzato and Cenedese were among the masters. Many of the sommerso pieces you find at flea markets today are from that period.

## Sommerso vs. cased glass

You'll also see "cased glass" — the English/American term for a similar idea. The difference is technical: cased glass usually has two layers (one colour inside, clear outside), while true sommerso often has three or more colour layers. In everyday language, though, the terms get used interchangeably.`,
    },
  },
  {
    slug: "mushroom-lampens-historie",
    kicker: { da: "Design", en: "Design" },
    cover: COVER_MUSHROOM,
    readingMinutes: 6,
    shopHandles: ["murano-mushroom-lampe", "murano-bordlamper"],
    da: {
      title: "Mushroom-lampens historie",
      excerpt:
        "Den runde, svampeformede bordlampe i mundblæst Murano-glas er blevet et af 70'ernes største comeback-stykker. Her er hvor den kommer fra.",
      body: `Mushroom-lampen — den runde glas-bordlampe med skærm og base i samme støbning — blev født på Murano i slutningen af 60'erne. Den er en af de mest umiddelbart genkendelige silhuetter fra italiensk mid-century design, og den har haft et vildt comeback i de sidste fem år.

## Hvor den kommer fra

De første mushroom-lamper fra Murano blev lavet hos værksteder som Vetri, Vistosi og Mazzega i sen-60'erne. De passede perfekt ind i tidens æstetik: bløde former, organiske farver, mod-følelsen fra Italien efter krigen. På samme tid lavede den franske designer Pierre Cardin lignende former i metal, og den danske Verner Panton arbejdede med plastik. Glas-versionen fra Murano blev hurtigt den varianten, samlere ledte efter.

## Hvordan de er lavet

En klassisk Murano-mushroom består af to dele: en hul base og en kuppelformet skærm, begge mundblæst. Lyset sidder inde i basen og skinner op gennem skærmen. Glasset er ofte swirl-mønstret — to farver der er snoet sammen — eller "candy" med farvede prikker indlagt.

Det store problem ved at lave dem er, at base og skærm skal passe præcist sammen. Hver lampe er lidt forskellig fra den næste — det er en del af charmen, men det er også grunden til at man skal være forsigtig når man køber: hvis basen og skærmen ikke matcher i størrelse eller farve, er det sandsynligvis to forskellige lamper sat sammen.

## Hvorfor de er populære nu

Mushroom-lampen var nede i mode fra 80'erne til omkring 2018. Så begyndte interiør-Instagram at finde den frem igen — først hos vintage-shops i New York og London, så i Paris, så i København. I dag er en god vintage Murano-mushroom et af de mest eftertragtede stykker i hele 70'er-revivalen.

## Hvad du skal betale

Priser varierer vildt. En lille mushroom (under 25 cm) i klassisk swirl koster typisk 1.500-3.000 kr i god stand. Store mega-mushrooms (40 cm+) kan ramme 6.000-12.000 kr og mere. Signerede stykker fra Vistosi eller Mazzega ligger højere.

På loppemarkedet finder vi dem stadig — sjældent, men det sker. Det er en del af grunden til at vi sælger. Når vi finder en mushroom til halv pris af butikspris, kan vi videregive den til en pris hvor du faktisk kan unde dig selv den.`,
    },
    en: {
      title: "A short history of the mushroom lamp",
      excerpt:
        "The round, mushroom-shaped table lamp in mouth-blown Murano glass is one of the biggest comeback pieces of the 70s. Here's where it comes from.",
      body: `The mushroom lamp — the round glass table lamp with shade and base in one form — was born on Murano in the late 1960s. It's one of the most immediately recognisable silhouettes in Italian mid-century design, and it's had a wild comeback in the last five years.

## Where it comes from

The first mushroom lamps from Murano were made at workshops like Vetri, Vistosi and Mazzega in the late 60s. They fit perfectly into the era's aesthetic: soft shapes, organic colours, the post-war Italian mod feeling. At the same time, the French designer Pierre Cardin was making similar shapes in metal, and Denmark's Verner Panton was working in plastic. The Murano glass version quickly became the one collectors looked for.

## How they're made

A classic Murano mushroom consists of two parts: a hollow base and a domed shade, both mouth-blown. The light sits inside the base and shines up through the shade. The glass is often swirl-patterned — two colours twisted together — or "candy" with coloured spots inlaid.

The hard part of making them is that base and shade have to fit precisely. Every lamp is slightly different from the next — that's part of the charm, but it's also why you have to be careful when buying: if the base and shade don't match in size or colour, it's probably two different lamps put together.

## Why they're popular now

The mushroom lamp went out of fashion from the 80s until around 2018. Then interior Instagram rediscovered it — first at vintage shops in New York and London, then Paris, then Copenhagen. Today a good vintage Murano mushroom is one of the most sought-after pieces in the whole 70s revival.

## What you should pay

Prices vary wildly. A small mushroom (under 25 cm) in classic swirl typically costs €200-400 in good condition. Large mega-mushrooms (40 cm+) can hit €800-1,600 and more. Signed pieces by Vistosi or Mazzega sit higher.

At the flea market we still find them — rarely, but it happens. It's part of why we do this. When we find a mushroom at half retail, we can pass it on at a price where you can actually treat yourself.`,
    },
  },
  {
    slug: "murano-vs-andet-italiensk-glas",
    kicker: { da: "Guide", en: "Guide" },
    cover: COVER_CEILING,
    readingMinutes: 5,
    shopHandles: ["murano-lamper", "vintage-bordlamper"],
    da: {
      title: "Murano vs. andet italiensk glas",
      excerpt:
        "Empoli, Pisa, Cenedese, Murano — italiensk glas kommer fra mange steder, ikke kun den ene ø. Her er forskellene der faktisk betyder noget.",
      body: `Når vi køber ind, skelner vi mellem Murano og "italiensk glas". Det er ikke fordi det andet er dårligt — det meste italienske vintage-glas er smukt, og noget af det er virkelig undervurderet. Men der er forskelle, og de påvirker pris, sjældenhed og sommetider også kvalitet.

## Murano (Veneto)

Den lille ø ud for Venedig. Glasværkstederne flyttede dertil i 1291 for at holde brandfaren væk fra Venedig selv. I dag er Murano stadig synonym med håndværk på højeste niveau. Mundblæst, ofte mere end ét lag, ofte signeret. Det er det dyreste italienske glas — fordi det er det mest komplekse at lave.

## Empoli (Toscana)

Empoli ligger uden for Firenze og var i 60-70'erne et af Italiens største centre for farvet glas — især det berømte "verde Empoli", den dybt grønne flaskefarve. Empoli-glas er typisk maskinpresset eller halvautomatisk, ikke mundblæst. Det er smukt, billigt, og fortjener mere kærlighed end det får. Hvis du finder en grøn vase eller karaffe på loppemarkedet — den er sandsynligvis Empoli.

## Cenedese (faktisk Murano)

Cenedese-værkstedet ligger på Murano men nævnes ofte separat fordi det specialiserede sig i sommerso. De har lavet nogle af de tungeste, mest dramatiske sommerso-stykker fra 50-60'erne.

## Industrielle italienske glasværker

Bormioli, Luminarc (delvis fransk), Arcoroc — det er fabriksglas, ikke håndværk. Også italiensk vintage, også flot, men i en helt anden kategori. Vi har en del af det i butikken til drikkeglas og daglig brug.

## Hvordan vi mærker forskellen

Murano: tungere, ofte uregelmæssigt i tykkelsen, har pontil-mærke, har dybde i farverne. Andet italiensk: lettere, mere ensartet, ofte med en sømn fra støbeformen, farverne mere "flade".

Begge dele har sin plads. Vi sælger begge dele. Men når vi siger Murano, mener vi Murano.`,
    },
    en: {
      title: "Murano vs. other Italian glass",
      excerpt:
        "Empoli, Pisa, Cenedese, Murano — Italian glass comes from many places, not just one island. Here are the differences that actually matter.",
      body: `When we buy in, we distinguish between Murano and "Italian glass". It's not that the other is bad — most Italian vintage glass is beautiful, and some of it is genuinely underrated. But there are differences, and they affect price, rarity and sometimes quality.

## Murano (Veneto)

The small island just off Venice. The glassworks moved there in 1291 to keep fire risk away from Venice itself. Today Murano is still synonymous with craftsmanship at the highest level. Mouth-blown, often more than one layer, often signed. It's the most expensive Italian glass — because it's the most complex to make.

## Empoli (Tuscany)

Empoli is outside Florence and in the 60s-70s was one of Italy's largest centres for coloured glass — especially the famous "verde Empoli", the deep bottle green. Empoli glass is typically machine-pressed or semi-automatic, not mouth-blown. It's beautiful, affordable, and deserves more love than it gets. If you find a green vase or carafe at a flea market — it's probably Empoli.

## Cenedese (technically Murano)

The Cenedese workshop is on Murano but is often listed separately because it specialised in sommerso. They made some of the heaviest, most dramatic sommerso pieces of the 50s-60s.

## Industrial Italian glassworks

Bormioli, Luminarc (partly French), Arcoroc — this is factory glass, not craft. Italian vintage too, also beautiful, but a completely different category. We carry some of it for drinking glasses and everyday use.

## How we tell them apart

Murano: heavier, often uneven in thickness, has a pontil mark, has depth in the colours. Other Italian: lighter, more uniform, often with a mould seam, colours more "flat".

Both have their place. We sell both. But when we say Murano, we mean Murano.`,
    },
  },
  {
    slug: "saadan-plejer-du-din-vintage-murano-lampe",
    kicker: { da: "Pleje", en: "Care" },
    cover: COVER_WALL,
    readingMinutes: 6,
    shopHandles: ["murano-lamper", "murano-loftlamper", "murano-vaeglamper"],
    da: {
      title: "Sådan plejer du din vintage Murano-lampe",
      excerpt:
        "Glasset er holdt i 60 år. Med lidt omtanke holder det 60 år til. Her er den korte guide til rengøring, ledninger og hvad du skal undgå.",
      body: `Murano-glas er overraskende holdbart — vi sælger jævnligt lamper fra 60'erne der ser ud næsten som nye. Men der er nogle få ting der kan gå galt, og som du let kan undgå.

## Rengøring

Brug aldrig opvaskemaskine. Aldrig. Murano-glas tåler ikke det høje hede + skarpe sæbe. Farverne kan blive matte, og decals/labels forsvinder.

I stedet:
- Lunkent vand med en lille smule mildt opvaskemiddel.
- En blød klud — gerne mikrofiber.
- Tør straks med en tør klud, så der ikke er kalkpletter.
- Til hjørner og indvendige kurver: en blød babyhårsbørste.

For lamper: tag dem ned, tag pæren ud, og skil glasdelene ad hvis muligt. Vask hver del for sig.

## Ledninger

Vintage-lamper kommer ofte med originale ledninger fra 60-70'erne. De er ikke nødvendigvis farlige, men de er gamle. Tegn på at en ledning skal udskiftes:
- Stift, sprøde isolering
- Synlige sprækker eller blankt kobber
- Varm når lampen brænder

Hvis du er i tvivl, lad en elektriker se på den. Det koster typisk 300-500 kr at få skiftet en ledning, og det er småpenge sammenlignet med en lampe til mange tusinde.

På de fleste lamper vi sælger er ledningerne tjekket eller udskiftet — vi noterer det altid i produktbeskrivelsen.

## Pærer

LED er fint. Brug noget med varmt lys (2700K eller lavere) — ellers ser glasset koldt og forkert ud. Maks 40W (eller LED-ækvivalent), især i lukkede plafonder hvor varmen ikke kan komme ud.

Aldrig halogen i lukkede glaslamper. For varmt.

## Opbevaring og flytning

Hvis lampen skal pakkes ned: skil den ad, vikl hver glasdel i blødt klæde eller bobleplast, og pak basen separat. Glas-på-glas i en flyttekasse er en sikker måde at få ridser.

## Hvis det går galt

Kommer der en revne i en skærm, så lad være med at bruge lampen — også selvom den ser fin ud udefra. En revnet skærm kan briste pludseligt. Skriv til os på butik@farveladeland.com hvis du vil prøve at finde en erstatningsskærm. Vi har ofte løsdele liggende fra lamper hvor den ene del er blevet slået i stykker.`,
    },
    en: {
      title: "How to care for your vintage Murano lamp",
      excerpt:
        "The glass has lasted 60 years. With a little care it will last 60 more. Here's the short guide to cleaning, wiring, and what to avoid.",
      body: `Murano glass is surprisingly durable — we regularly sell lamps from the 60s that look almost new. But there are a few things that can go wrong, and that you can easily avoid.

## Cleaning

Never use the dishwasher. Ever. Murano glass can't handle the high heat plus harsh detergent. Colours can go matt, and decals/labels disappear.

Instead:
- Lukewarm water with a small amount of mild dish soap.
- A soft cloth — microfibre is good.
- Dry immediately with a dry cloth, to avoid limescale spots.
- For corners and inner curves: a soft baby hair brush.

For lamps: take them down, remove the bulb, and separate the glass parts if possible. Wash each part on its own.

## Wiring

Vintage lamps often come with original wiring from the 60s-70s. They're not necessarily dangerous, but they're old. Signs that a cord needs replacing:
- Stiff, brittle insulation
- Visible cracks or exposed copper
- Warm when the lamp is on

If in doubt, have an electrician look at it. It typically costs €40-70 to have a cord replaced — small money compared to a lamp worth several hundred.

On most lamps we sell, the wiring has been checked or replaced — we always note it in the product description.

## Bulbs

LED is fine. Use something with warm light (2700K or lower) — otherwise the glass looks cold and wrong. Max 40W (or LED equivalent), especially in closed plafonds where the heat can't escape.

Never halogen in closed glass lamps. Too hot.

## Storage and moving

If the lamp needs to be packed away: take it apart, wrap each glass part in soft cloth or bubble wrap, and pack the base separately. Glass-on-glass in a moving box is a sure way to get scratches.

## When it goes wrong

If a shade cracks, stop using the lamp — even if it looks fine from outside. A cracked shade can break suddenly. Email us at butik@farveladeland.com if you want to try to find a replacement shade. We often have loose parts lying around from lamps where one part got broken.`,
    },
  },
];

export function getLeksikonArticle(slug: string): LeksikonArticle | undefined {
  return leksikonArticles.find((a) => a.slug === slug);
}
